import React, { Fragment, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AffectOutput,
  CreationAccountMeasure,
  CreationAccountRow,
  GetCreationAccountMeasuresOrderEnum,
  PatchMeasureInput,
  PostMeasureInput,
} from '@/api/creation';
import { Button } from '@/components/Elements';
import { Pagenation, PageSize } from '@/components/Elements/Pagenation/Pagenation';
import { deleteMeasure } from '@/hooks/Creation';
import { getImageUrl } from '@/utils/image';

import { ReactComponent as PlusImage } from './assets/bx-plus.svg';
import { ReactComponent as PRTwoColorImage } from './assets/PR_Two Color 1.svg';
import DotsDropdown from './DotsDropdown';
import ProjectModal from './ProjectModal';

interface ProjectListProps {
  measures: CreationAccountMeasure[];
  totalRows: number;
  selectAccount: CreationAccountRow;
  updateMeasures: (
    perPage?: PageSize,
    currentPage?: number,
    order?: GetCreationAccountMeasuresOrderEnum,
    orderBy?: string
  ) => void;
  addMeasure: (input: PostMeasureInput) => Promise<AffectOutput>;
  editMeasure: (measureId: string, input: PatchMeasureInput) => Promise<AffectOutput>;
}

export interface ProjectModalStatus {
  open: boolean;
  status:
    | {
        type: 'create';
      }
    | {
        type: 'edit';
        measureId: string;
      };
  defaultValue: string;
}

export const ProjectListView = (props: ProjectListProps) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<PageSize>(10);
  const [projectModalStatus, setProjectModalStatus] = useState<ProjectModalStatus>({
    open: false,
    status: {
      type: 'create',
    },
    defaultValue: '',
  });

  const addMeasure = (input: PostMeasureInput) => {
    props.addMeasure(input).then(() => {
      props.updateMeasures(perPage, currentPage, 'desc');
    });
  };
  const editMeasure = (measureId: string, input: PatchMeasureInput) => {
    props.editMeasure(measureId, input).then(() => {
      props.updateMeasures(perPage, currentPage, 'desc');
    });
  };

  useEffect(() => {
    props.updateMeasures(perPage, currentPage, 'desc');
  }, [currentPage, perPage, props.selectAccount]);

  const selectProject = (measureId: string) => () => {
    navigate(`/PRST_production_management/${measureId}`); // 遷移時にmeasureIdを渡す
  };

  const replaceDate = (date: string) => {
    const newDate = new Date(date);
    return newDate.toLocaleDateString();
  };

  return (
    <>
      <div className="w-full max-w-[940px] bg-gray-50 px-[24px] py-[16px]">
        <div className="w-full max-w-[840px]">
          <div className="mb-[16px] flex h-[38px] w-full max-w-[840px] justify-between">
            <p className="text-base font-semibold leading-7 text-gray-700">施策一覧</p>
            <Button
              startIcon={<PlusImage className="h-[20px] w-[20px]" />}
              size="md"
              className="whitespace-nowrap text-sm font-medium leading-5"
              style={{
                paddingTop: '9px',
                paddingBottom: '9px',
                paddingLeft: '10px',
                paddingRight: '12px',
              }}
              onClick={() => {
                setProjectModalStatus({
                  open: true,
                  status: {
                    type: 'create',
                  },
                  defaultValue: '',
                });
              }}
            >
              施策を追加
            </Button>
          </div>
        </div>
        {props.measures.length > 0 ? (
          <div className="w-full max-w-[840px] rounded bg-[#FFFFFF] px-[18px] pt-[35px]">
            <table className="mb-5 h-[356px] w-full">
              {/* 1列目 */}
              <thead>
                <tr className="flex items-start">
                  <th scope="col" className="sticky py-[6px] pl-4 text-left" style={{ flex: `0 0 ${191 / 8.03}%` }}>
                    <span className="text-xs font-normal leading-4 tracking-wider text-gray-700">施策名</span>
                  </th>
                  <th scope="col" className="sticky py-[6px] pl-4 text-left" style={{ flex: `0 0 ${165 / 8.03}%` }}>
                    <span className="text-xs font-normal leading-4 tracking-wider text-gray-700">作成日</span>
                  </th>
                  <th scope="col" className="sticky py-[6px] pl-4 text-left" style={{ flex: `0 0 ${92 / 8.03}%` }}>
                    <span className="text-xs font-normal leading-4 tracking-wider text-gray-700">投稿予定数</span>
                  </th>
                  <th scope="col" className="sticky py-[6px] pl-4 text-left" style={{ flex: `0 0 ${92 / 8.03}%` }}>
                    <span className="text-xs font-normal leading-4 tracking-wider text-gray-700">投稿完了数</span>
                  </th>
                  <th scope="col" className="sticky py-[6px] pl-4 text-left" style={{ flex: `0 0 ${145 / 8.03}%` }}>
                    <span className="text-xs font-normal leading-4 tracking-wider text-gray-700">クリエイター</span>
                  </th>
                  <th scope="col" className="sticky py-[6px] pl-4 text-left" style={{ flex: `1 1 ${118 / 8.03}%` }}>
                    <span className="text-xs font-normal leading-4 tracking-wider text-gray-700"></span>
                  </th>
                </tr>
              </thead>
              {/* 2列目 */}
              <tbody className="flex h-[320px] w-full max-w-[803px] flex-col overflow-x-hidden overflow-y-scroll bg-white">
                {props.measures.map((project, index) => (
                  <tr
                    key={project.id}
                    className={`flex cursor-pointer ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                    style={{ flex: '0 0 64px' }}
                  >
                    <td style={{ flex: `0 0 ${191 / 7.86}%` }}>
                      <button onClick={selectProject(project.id)} className="h-full w-full">
                        <div className="flex items-center pl-4">
                          <div className="line-clamp-2 text-sm font-normal leading-5 text-gray-700 ">
                            <div style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{project.name}</div>
                          </div>
                        </div>
                      </button>
                    </td>
                    <td style={{ flex: `0 0 ${165 / 7.86}%` }}>
                      <button onClick={selectProject(project.id)} className="h-full w-full">
                        <div className="flex items-center pl-4">
                          <div className="text-sm font-normal leading-5 text-gray-700">
                            {replaceDate(project.createdAt)}
                          </div>
                        </div>
                      </button>
                    </td>
                    <td style={{ flex: `0 0 ${92 / 7.86}%` }}>
                      <button onClick={selectProject(project.id)} className="h-full w-full">
                        <div className="flex items-center pl-4">
                          <div className="text-sm font-normal leading-5 text-gray-700">{project.postsPlan}</div>
                        </div>
                      </button>
                    </td>
                    <td style={{ flex: `0 0 ${92 / 7.86}%` }}>
                      <button onClick={selectProject(project.id)} className="h-full w-full">
                        <div className="flex items-center pl-4">
                          <div className="text-sm font-normal leading-5 text-gray-700">{project.postsResult}</div>
                        </div>
                      </button>
                    </td>
                    <td style={{ flex: `0 0 ${145 / 7.86}%` }}>
                      <button onClick={selectProject(project.id)} className="h-full w-full">
                        <div className="flex items-center pl-4">
                          <div className="text-sm font-normal leading-5 text-gray-700">
                            {project.influencerSnsAccounts.rows.length === 0 ? (
                              // rowsが空の場合は未招待を表示
                              <p className="h-[20px] w-[56px] rounded bg-[#F3F4F6] align-middle text-xs font-medium text-[#1F2937]">
                                未招待
                              </p>
                            ) : (
                              // rowsが空でない場合は画像とその情報を表示
                              // rowsは現在時点だと1件しか返却されないため、[0]の中身を使用する
                              <>
                                <div className="flex items-center">
                                  <img
                                    src={getImageUrl(project.influencerSnsAccounts.rows[0].thumbnail || '')}
                                    alt="thumbnail"
                                    className="h-[24px] w-[24px] rounded-full"
                                  />
                                  <p>{project.influencerSnsAccounts.rows[0].screenName}</p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                    </td>
                    <td style={{ flex: `1 1 ${118 / 7.86}%` }}>
                      <div className="h-full w-full">
                        <div className="flex h-full w-full items-center">
                          <DotsDropdown
                            modalText="この施策を"
                            listType="project"
                            deleteFunction={() => {
                              deleteMeasure(project.id).then(() => {
                                props.updateMeasures(perPage, currentPage, 'desc');
                              });
                            }}
                            editFunction={() => {
                              setProjectModalStatus({
                                open: true,
                                status: {
                                  type: 'edit',
                                  measureId: project.id,
                                },
                                defaultValue: project.name,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {props.measures.length && (
              <Pagenation
                currentPageNumber={currentPage}
                currentPageSize={perPage}
                togalDataCount={props.totalRows}
                totalPageNumber={Math.ceil(props.totalRows / perPage)}
                setPageNumber={setCurrentPage}
                setPageSize={setPerPage}
              />
            )}
          </div>
        ) : (
          <div className="h-[525px] w-full max-w-[892px] rounded bg-white px-[20px] pb-[38px] pt-[24px] shadow">
            <div className="flex h-[463px] w-full items-center justify-center">
              <div className="flex flex-col items-center justify-center">
                <PRTwoColorImage width={'100%'} />
                <div className="flex justify-center">
                  <p className="w-[294px] text-center text-[14px] font-normal leading-6 text-[#6B7280]">
                    まだ施策がありません。
                    <br />
                    右上のボタンから施策を作成してみましょう！
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="relative">
        <ProjectModal
          modalStatus={projectModalStatus}
          setModalStatus={setProjectModalStatus}
          addMeasure={addMeasure}
          editMeasure={editMeasure}
        />
      </div>
    </>
  );
};
